/**
 * SVG (レイヤー素材) のアップロードを受けるか。3 タブ目「モーショングラフィックス」の表示条件と同じ
 * (ブランドのフラグ `motionGraphicsPromptMode` かつ `GET /api/motion-layered/availability` が enabled・
 * motion-graphics-mode-plan.md §5.1・§10-3)。生成できない (料金行なし・レンダラー未対応・暫定ゲート) 間に
 * SVG だけ受けても使い道が無いので、タブと同じく fail-closed で閉じる。
 *
 * 素材パネル / ホーム / アップロードモーダルはログイン中のほぼ全ページに出るので、ここでは availability を
 * eager に取りに行かない (useQuery を張らない)。SVG がドロップ / 選択されたときに初めて
 * queryClient.fetchQuery で確認する (取得済みで新鮮ならキャッシュを使う・動画プロンプト画面のタブ判定と同じキー)。
 * - acceptSvg: `<input accept>` に `.svg` を含めるか (フラグ かつ ログイン中)。受け入れの最終判定ではない
 * - resolveAllowSvg(files): files に SVG が無ければ取りに行かず false。あれば availability を確認して受けるか返す
 * react-router に依存しないので Router の外 (RightPanelWrapper) でも使える (QueryClientProvider の中であればよい)。
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { brandConfig } from '../../config/brands';
import { useUser } from '../auth/useUser';
import { motionLayeredAvailabilityQueryOptions } from '../motion/availability';
import { shouldShowMotionGraphicsTab } from '../motion/layeredPromptMode';
import { isSvgFile } from './svgLayerUpload';

export type SvgLayerUploadGate = {
  acceptSvg: boolean;
  resolveAllowSvg: (files: ReadonlyArray<{ name: string; type: string }>) => Promise<boolean>;
};

export function useSvgLayerUploadGate(): SvgLayerUploadGate {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useUser();
  const flag = brandConfig.features.motionGraphicsPromptMode === true;
  const acceptSvg = flag && isAuthenticated;
  const userKey = user?.user_uid ?? user?.sub ?? null;
  const organizationId = user?.organization_id ?? null;
  const resolveAllowSvg = useCallback(async (files: ReadonlyArray<{ name: string; type: string }>) => {
    if (!files.some((f) => isSvgFile(f))) return false;
    if (!acceptSvg) return false;
    try {
      const availability = await queryClient.fetchQuery(motionLayeredAvailabilityQueryOptions(userKey, organizationId));
      return shouldShowMotionGraphicsTab({ flag, availability });
    } catch {
      return false; // 取得失敗は受けない (fail-closed)
    }
  }, [acceptSvg, flag, organizationId, queryClient, userKey]);
  return { acceptSvg, resolveAllowSvg };
}
